(() => {
  if (window.__WFHLandingLoaded) return;
  window.__WFHLandingLoaded = true;

  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart`;
  const addUrl = `${root}cart/add.js`;

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  /* -----------------------------------------------------------------------
     EXISTING THEME CART DRAWER ONLY

     IMPORTANT:
     We do NOT use cart-drawer.renderContents() here.

     On this store, the theme's partial section response updates the
     shipping/total/footer areas but does not correctly repopulate the custom
     line-item area. A normal page refresh DOES render the drawer correctly.

     So after Shopify adds the item, we fetch the current page once in the
     background, take the freshly server-rendered EXISTING <cart-drawer> and
     header cart trigger, replace their stale DOM, then open the theme drawer.
     This is effectively a refresh of the native cart UI only.
  ----------------------------------------------------------------------- */

  const getExistingThemeDrawer = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) throw new Error('Existing theme <cart-drawer> was not found');
    return drawer;
  };

  const getHeaderCartTrigger = (rootNode = document) => {
    return (
      rootNode.querySelector('#cart-icon-bubble') ||
      rootNode.querySelector('header a[href$="/cart"]') ||
      rootNode.querySelector('header a[href*="/cart?"]')
    );
  };

  const fetchFreshThemeDocument = async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('_wfh_native_cart_refresh', Date.now().toString());

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'text/html'
      }
    });

    if (!response.ok) {
      throw new Error(`Could not refresh native cart markup (${response.status})`);
    }

    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

  const refreshAndOpenExistingThemeCart = async activeElement => {
    // The cart session is already updated at this point by /cart/add.js.
    // Fetching the page now gives us the exact cart drawer markup that a real
    // browser refresh would show.
    const freshDocument = await fetchFreshThemeDocument();

    const liveDrawer = getExistingThemeDrawer();
    const freshDrawer = freshDocument.querySelector('cart-drawer');

    if (!freshDrawer) {
      throw new Error('Fresh page did not contain the theme <cart-drawer>');
    }

    // Replace the header cart trigger FIRST. When the new cart-drawer custom
    // element connects, the theme can bind against the fresh header trigger.
    const liveTrigger = getHeaderCartTrigger(document);
    const freshTrigger = getHeaderCartTrigger(freshDocument);

    if (liveTrigger && freshTrigger) {
      liveTrigger.replaceWith(document.importNode(freshTrigger, true));
    }

    // Replace only the THEME'S cart drawer with the freshly server-rendered
    // version from this same storefront. We are not creating a different cart
    // UI or custom drawer.
    const freshDrawerNode = document.importNode(freshDrawer, true);
    liveDrawer.replaceWith(freshDrawerNode);

    // Allow the browser to upgrade/reconnect the existing theme custom element.
    if (window.customElements?.whenDefined) {
      await customElements.whenDefined('cart-drawer');
    }
    await nextFrame();
    await nextFrame();

    const drawer = getExistingThemeDrawer();

    if (typeof drawer.setActiveElement === 'function' && activeElement) {
      drawer.setActiveElement(activeElement);
    }

    // Use the theme's own open method whenever available.
    if (typeof drawer.open === 'function') {
      drawer.open(activeElement);
      return;
    }

    // Minimal fallback only if the theme exposes no open() method.
    drawer.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
  };

  /* -----------------------------------------------------------------------
     PRODUCT GALLERIES: native swipe + arrows + dots
  ----------------------------------------------------------------------- */

  const goToSlide = (gallery, index, smooth = true) => {
    const track = gallery?.querySelector('[data-wfh-media-track]');
    const slides = [...(gallery?.querySelectorAll('[data-wfh-media-slide]') || [])];
    if (!track || !slides.length) return;
    const safeIndex = Math.max(0, Math.min(index, slides.length - 1));
    track.scrollTo({ left: slides[safeIndex].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    gallery.querySelectorAll('[data-wfh-gallery-dot]').forEach((dot, i) => dot.classList.toggle('is-active', i === safeIndex));
  };

  const initializeGallery = gallery => {
    if (gallery.dataset.wfhGalleryReady === 'true') return;
    gallery.dataset.wfhGalleryReady = 'true';
    const track = gallery.querySelector('[data-wfh-media-track]');
    const slides = [...gallery.querySelectorAll('[data-wfh-media-slide]')];
    if (!track || slides.length < 2) return;

    gallery.querySelector('[data-wfh-gallery-prev]')?.addEventListener('click', event => {
      event.preventDefault();
      const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      goToSlide(gallery, index - 1);
    });
    gallery.querySelector('[data-wfh-gallery-next]')?.addEventListener('click', event => {
      event.preventDefault();
      const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      goToSlide(gallery, index + 1);
    });

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        gallery.querySelectorAll('[data-wfh-gallery-dot]').forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        ticking = false;
      });
    }, { passive: true });
  };

  document.querySelectorAll('[data-wfh-media-gallery]').forEach(initializeGallery);

  /* -----------------------------------------------------------------------
     VARIANT OPTIONS + IMAGE SYNC
  ----------------------------------------------------------------------- */

  const getVariants = card => {
    try { return JSON.parse(card.querySelector('[data-wfh-variants]')?.textContent || '[]'); }
    catch (_) { return []; }
  };

  const getVariantMediaMap = card => {
    try { return JSON.parse(card.querySelector('[data-wfh-variant-media]')?.textContent || '{}'); }
    catch (_) { return {}; }
  };

  const showVariantImage = (card, variant) => {
    if (!variant) return;
    const mediaMap = getVariantMediaMap(card);
    const mediaId = mediaMap[String(variant.id)] || variant.featured_media?.id || variant.featured_image?.id;
    if (!mediaId) return;
    const gallery = card.querySelector('[data-wfh-media-gallery]');
    const slide = gallery?.querySelector(`[data-wfh-media-slide][data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!gallery || !slide) return;
    const slides = [...gallery.querySelectorAll('[data-wfh-media-slide]')];
    goToSlide(gallery, slides.indexOf(slide));
  };

  document.addEventListener('click', event => {
    const optionButton = event.target.closest('[data-wfh-option-value]');
    if (!optionButton) return;

    event.preventDefault();
    const card = optionButton.closest('[data-wfh-product-card]');
    const fieldset = optionButton.closest('[data-wfh-option-index]');
    if (!card || !fieldset) return;

    fieldset.querySelectorAll('[data-wfh-option-value]').forEach(btn => btn.classList.remove('is-selected'));
    optionButton.classList.add('is-selected');

    const variants = getVariants(card);
    const changedIndex = Number(fieldset.dataset.wfhOptionIndex || 0);
    const changedValue = optionButton.dataset.wfhOptionValue || '';

    // Immediately give a visual reference for the newly chosen color/option,
    // even before resolving the full size + color variant combination.
    const visualVariant = variants.find(v => v.options?.[changedIndex] === changedValue && (getVariantMediaMap(card)[String(v.id)] || v.featured_image));
    showVariantImage(card, visualVariant);

    const chosen = [...card.querySelectorAll('[data-wfh-option-index]')]
      .sort((a, b) => Number(a.dataset.wfhOptionIndex) - Number(b.dataset.wfhOptionIndex))
      .map(group => group.querySelector('.is-selected')?.dataset.wfhOptionValue || '');

    const variant = variants.find(v => v.options?.every((value, i) => value === chosen[i]));
    const idInput = card.querySelector('input[name="id"]');
    const addButton = card.querySelector('[data-wfh-add]');
    const price = card.querySelector('[data-wfh-price]');
    const compare = card.querySelector('[data-wfh-compare-price]');

    if (variant) {
      if (idInput) idInput.value = variant.id;
      if (price) price.textContent = money(variant.price, card.dataset.currency);
      if (compare) {
        const hasCompare = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        compare.hidden = !hasCompare;
        compare.textContent = hasCompare ? money(variant.compare_at_price, card.dataset.currency) : '';
      }
      showVariantImage(card, variant);
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? addButton.dataset.availableText : addButton.dataset.soldText;
      }
    } else if (addButton) {
      addButton.disabled = true;
      addButton.textContent = addButton.dataset.unavailableText;
    }
  });

  /* -----------------------------------------------------------------------
     ADD TO BAG -> RENDER + OPEN EXISTING THEME CART DRAWER
  ----------------------------------------------------------------------- */

  document.addEventListener('submit', async event => {
    const form = event.target.closest('.wfh-product__form');
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('[data-wfh-add]');
    const originalText = button?.textContent;
    form.classList.add('is-loading');
    if (button) {
      button.disabled = true;
      button.textContent = button.dataset.loadingText || 'adding...';
    }

    try {
      const formData = new FormData(form);

      const response = await fetch(addUrl, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'Add to cart failed');

      if (button) button.textContent = button.dataset.addedText || 'added ✓';

      // Do not feed a partial section response into renderContents().
      // Refresh only the native theme cart DOM from the same server-rendered
      // page that we already know is correct after a browser refresh.
      await refreshAndOpenExistingThemeCart(button || document.activeElement);

      setTimeout(() => {
        if (!button) return;
        button.disabled = false;
        button.textContent = button.dataset.availableText || originalText || 'add to bag';
      }, 1200);
    } catch (error) {
      console.error('[WFH] add to cart:', error);
      if (button) {
        button.textContent = 'please try again';
        setTimeout(() => {
          button.disabled = false;
          button.textContent = originalText || button.dataset.availableText || 'add to bag';
        }, 1600);
      }
    } finally {
      form.classList.remove('is-loading');
    }
  });

  /* -----------------------------------------------------------------------
     SET BUILDER
  ----------------------------------------------------------------------- */

  const setBuilder = document.querySelector('[data-wfh-set-builder]');
  if (setBuilder) {
    const selects = [...setBuilder.querySelectorAll('[data-wfh-set-select]')];
    const total = setBuilder.querySelector('[data-wfh-set-total]');
    const button = setBuilder.querySelector('[data-wfh-set-add]');

    const updateSet = () => {
      const sum = selects.reduce((acc, select) => acc + Number(select.selectedOptions[0]?.dataset.price || 0), 0);
      if (total) total.textContent = money(sum, setBuilder.dataset.currency);
      if (button) button.disabled = selects.some(select => !select.value || select.selectedOptions[0]?.disabled);

      selects.forEach(select => {
        const image = select.selectedOptions[0]?.dataset.image;
        const card = select.closest('[data-wfh-set-card]');
        const img = card?.querySelector('.wfh-set-card__media img');
        if (image && img) img.src = image;
      });
    };

    selects.forEach(select => select.addEventListener('change', updateSet));
    updateSet();

    button?.addEventListener('click', async () => {
      const items = selects.map(select => ({ id: Number(select.value), quantity: 1 })).filter(item => item.id);
      if (items.length !== selects.length) return;

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = button.dataset.loadingText || 'adding set...';

      try {
        const payload = { items };

        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Could not add set');

        button.textContent = button.dataset.addedText || 'set added ✓';
        await refreshAndOpenExistingThemeCart(button);
        setTimeout(() => { button.textContent = originalText; button.disabled = false; }, 1300);
      } catch (error) {
        console.error('[WFH] set builder:', error);
        button.textContent = 'please try again';
        setTimeout(() => { button.textContent = originalText; button.disabled = false; }, 1600);
      }
    });
  }

})();
